// frontend/src/components/sales/PendingSales.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard, printSaleReceipt } from '../common/CommonComponents';

function PendingSales({ showAlert, user }) {
  const [pending, setPending] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [expandedSale, setExpandedSale] = useState(null);
  const [filter, setFilter] = useState("all");
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [completedSale, setCompletedSale] = useState(null);

  const isManager = user.role === "manager";

  // Use a ref to track if component is mounted
  const isMounted = useRef(true);
  
  // Use a ref to store pending for silent updates without causing re-renders that flicker
  const pendingRef = useRef(pending);
  
  // Update ref when pending changes
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  // Fetch data without showing loading spinner (silent)
  const fetchDataSilently = useCallback(async (showToast = false) => {
    try {
      const data = await api.getPendingSales();
      if (isMounted.current) {
        // Only update if data actually changed to avoid unnecessary re-renders
        const currentIds = new Set(pendingRef.current.map(s => s.saleId));
        const newIds = new Set(data.map(s => s.saleId));
        
        // Check if data has changed
        const hasChanged = currentIds.size !== newIds.size || 
          JSON.stringify(pendingRef.current.map(s => s.saleId).sort()) !== 
          JSON.stringify(data.map(s => s.saleId).sort());
        
        if (hasChanged) {
          setPending(data);
          setLastUpdateTime(new Date());
          if (showToast) {
            showAlert(`Updated - ${data.length} pending sale${data.length !== 1 ? "s" : ""}`, "info");
          }
        } else if (showToast) {
          showAlert("No changes since last refresh", "info");
        }
      }
    } catch (e) {
      console.error("Silent refresh failed:", e);
      if (showToast) {
        showAlert("Failed to refresh", "error");
      }
    }
  }, [showAlert]);

  // Initial load with spinner
  const initialLoad = useCallback(async () => {
    try {
      const data = await api.getPendingSales();
      if (isMounted.current) {
        setPending(data);
        setLastUpdateTime(new Date());
      }
    } catch (e) {
      showAlert("Failed to load pending sales", "error");
    } finally {
      if (isMounted.current) {
        setIsInitialLoad(false);
      }
    }
  }, [showAlert]);

  // Load on mount
  useEffect(() => {
    isMounted.current = true;
    initialLoad();
    
    return () => {
      isMounted.current = false;
    };
  }, [initialLoad]);

  // Silent background refresh every 30 seconds - NO RE-RENDER FLICKER
  useEffect(() => {
    if (isInitialLoad) return;
    
    const interval = setInterval(() => {
      // Use requestAnimationFrame to batch updates and prevent flicker
      requestAnimationFrame(() => {
        fetchDataSilently(false);
      });
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isInitialLoad, fetchDataSilently]);

  // Refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !isInitialLoad && isMounted.current) {
        requestAnimationFrame(() => {
          fetchDataSilently(false);
        });
      }
    };
    
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isInitialLoad, fetchDataSilently]);

  const process = async (id) => {
    if (!window.confirm("Confirm that payment has been received for this sale?\n\nThis will:\n✓ Update stock levels\n✓ Record revenue and profit\n✓ Add to sales log\n\nThis cannot be undone.")) return;
    
    setProcessingId(id);
    try {
      const processed = await api.processSale(id);
      setCompletedSale(processed);
      showAlert("Sale processed! Revenue and profit recorded.", "success");
      // Remove the processed sale from local state immediately (optimistic update)
      setPending(prev => prev.filter(sale => sale.saleId !== id));
      // Then silently refresh to ensure consistency
      requestAnimationFrame(() => {
        fetchDataSilently(false);
      });
    } catch (e) {
      showAlert(e.message, "error");
    }
    setProcessingId(null);
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this sale?\n\nThis will restore stock levels and cannot be undone.")) return;
    
    setProcessingId(id);
    try {
      await api.cancelSale(id);
      showAlert("Sale cancelled. Stock restored.", "info");
      // Remove the cancelled sale from local state immediately
      setPending(prev => prev.filter(sale => sale.saleId !== id));
      requestAnimationFrame(() => {
        fetchDataSilently(false);
      });
    } catch (e) {
      showAlert(e.message, "error");
    }
    setProcessingId(null);
  };

  // Every sale on this screen is, by definition, Awaiting Payment (see
  // GET /api/sales/pending, routes/sales.js) — printing it as a
  // "receipt" here would misrepresent money as already collected before
  // it actually has been. Always invoice mode on this screen.
  const printReceipt = (sale) => {
    printSaleReceipt(sale, user.businessName, user.businessLogo, user.planFeatures?.hasCustomBranding, "invoice");
  };

  const shareCompletedReceipt = async () => {
    if (!completedSale) return;
    const popup = window.open("", "_blank", "noreferrer");
    try {
      const links = await api.getSaleWhatsappLinks(completedSale.saleId);
      if (!links.hasPhone || !links.receiptLink) {
        if (popup) popup.close();
        showAlert("This sale's customer needs a phone number before a WhatsApp receipt can be shared.", "error");
        return;
      }
      if (popup) popup.location.href = links.receiptLink;
      else window.open(links.receiptLink, "_blank", "noreferrer");
    } catch (e) {
      if (popup) popup.close();
      showAlert(e.message, "error");
    }
  };

  const handleManualRefresh = async () => {
    await fetchDataSilently(true);
  };

  // Filter pending sales (using memo to prevent unnecessary recalculations)
  const filteredPending = useMemo(() => {
    return pending.filter(sale => {
      if (filter === "all") return true;
      if (filter === "cash") return sale.paymentMethod === "Cash";
      if (filter === "transfer") return sale.paymentMethod === "Transfer";
      if (filter === "pos") return sale.paymentMethod === "POS";
      return true;
    });
  }, [pending, filter]);

  // Calculate totals (using memo)
  const totals = useMemo(() => {
    const totalAmount = filteredPending.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
    const cashTotal = filteredPending.filter(s => s.paymentMethod === "Cash").reduce((sum, s) => sum + (s.totalAmount || 0), 0);
    const transferTotal = filteredPending.filter(s => s.paymentMethod === "Transfer").reduce((sum, s) => sum + (s.totalAmount || 0), 0);
    const posTotal = filteredPending.filter(s => s.paymentMethod === "POS").reduce((sum, s) => sum + (s.totalAmount || 0), 0);
    return { totalAmount, cashTotal, transferTotal, posTotal };
  }, [filteredPending]);

  // Method color mapping
  const getMethodColor = (method) => {
    const colors = {
      "Cash": { bg: "#10B98120", text: "#10B981", icon: "💵" },
      "Transfer": { bg: "#3B82F620", text: "#3B82F6", icon: "🏦" },
      "POS": { bg: "#F59E0B20", text: "#F59E0B", icon: "💳" }
    };
    return colors[method] || { bg: "#94A3B820", text: "#94A3B8", icon: "💰" };
  };

  // Show loading spinner only on first load
  if (isInitialLoad) {
    return <div className="spinner">Loading pending sales...</div>;
  }

  return (
    <div>
      {/* Header Section */}
      <div className="section-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>✅ Confirm Walk-in Sales</h2>
          <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>
            Review and confirm sales after payment is collected
          </p>
          {lastUpdateTime && (
            <p style={{ fontSize: 10, color: "var(--text-3)", marginTop: 2 }}>
              Last updated: {lastUpdateTime.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button 
            className="btn-sm btn-secondary" 
            onClick={handleManualRefresh}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {completedSale && (
        <div className="alert alert-success" style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <span>Sale {completedSale.saleId} was confirmed and its receipt is ready.</span>
          <div style={{ display: "flex", gap: 8 }}>
            {user.planFeatures?.hasWhatsappTools !== false && <button className="btn-sm btn-approve" onClick={shareCompletedReceipt}>Share Receipt</button>}
            <button className="btn-sm btn-secondary" onClick={() => setCompletedSale(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Stats Summary Cards */}
      {filteredPending.length > 0 && (
        <div className="stats-grid-4" style={{ marginBottom: 16 }}>
          <div className="stat-card" style={{ borderLeft: "4px solid #3B82F6" }}>
            <div className="lbl">Pending Sales</div>
            <div className="val" style={{ fontSize: 20 }}>{filteredPending.length}</div>
            <div className="stat-sub">Awaiting confirmation</div>
          </div>
          <div className="stat-card" style={{ borderLeft: "4px solid #10B981" }}>
            <div className="lbl">Total Amount</div>
            <div className="val success">{fmt(totals.totalAmount)}</div>
            <div className="stat-sub">To be collected</div>
          </div>
          <div className="stat-card" style={{ borderLeft: "4px solid #F59E0B" }}>
            <div className="lbl">By Cash</div>
            <div className="val warning">{fmt(totals.cashTotal)}</div>
            <div className="stat-sub">{filteredPending.filter(s => s.paymentMethod === "Cash").length} sales</div>
          </div>
          <div className="stat-card" style={{ borderLeft: "4px solid #8B5CF6" }}>
            <div className="lbl">By Transfer/POS</div>
            <div className="val" style={{ color: "#8B5CF6" }}>{fmt(totals.transferTotal + totals.posTotal)}</div>
            <div className="stat-sub">Verify before confirming</div>
          </div>
        </div>
      )}

      {/* Filter Buttons */}
      {pending.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <button
            className={`btn-sm ${filter === "all" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setFilter("all")}
          >
            All ({pending.length})
          </button>
          <button
            className={`btn-sm ${filter === "cash" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setFilter("cash")}
          >
            💵 Cash ({pending.filter(s => s.paymentMethod === "Cash").length})
          </button>
          <button
            className={`btn-sm ${filter === "transfer" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setFilter("transfer")}
          >
            🏦 Transfer ({pending.filter(s => s.paymentMethod === "Transfer").length})
          </button>
          <button
            className={`btn-sm ${filter === "pos" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setFilter("pos")}
          >
            💳 POS ({pending.filter(s => s.paymentMethod === "POS").length})
          </button>
        </div>
      )}

      {/* Sales List */}
      {filteredPending.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
          <h3 style={{ marginBottom: 8, color: "var(--text)" }}>All caught up!</h3>
          <p style={{ color: "var(--text-3)", marginBottom: 16 }}>
            No pending sales awaiting confirmation.
          </p>
          {pending.length > 0 && filter !== "all" && (
            <button className="btn btn-secondary" onClick={() => setFilter("all")}>
              View all pending sales
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filteredPending.map(sale => {
            const methodStyle = getMethodColor(sale.paymentMethod);
            const isExpanded = expandedSale === sale.saleId;
            const isProcessing = processingId === sale.saleId;

            return (
              <div key={sale.saleId} className="card" style={{ 
                borderLeft: `4px solid ${methodStyle.text}`,
                transition: "all 0.2s",
                opacity: isProcessing ? 0.6 : 1
              }}>
                {/* Sale Header */}
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "flex-start", 
                  flexWrap: "wrap", 
                  gap: 12,
                  marginBottom: isExpanded ? 12 : 0
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ 
                      background: methodStyle.bg, 
                      padding: "6px 12px", 
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 6
                    }}>
                      <span>{methodStyle.icon}</span>
                      <span style={{ fontWeight: 700, color: methodStyle.text, fontSize: 12 }}>{sale.paymentMethod}</span>
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontFamily: "monospace", fontSize: 11, color: "var(--text-2)" }}>
                          #{sale.saleId}
                        </span>
                        {sale.paymentReference && (
                          <span style={{ fontSize: 10, background: "var(--surface2)", padding: "2px 6px", borderRadius: 4 }}>
                            Ref: {sale.paymentReference}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                        {new Date(sale.createdAt).toLocaleString()} · by {sale.loggedBy}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "var(--accent)" }}>{fmt(sale.totalAmount)}</div>
                    {sale.discountAmount > 0 && (
                      <div style={{ fontSize: 10, color: "var(--red)" }}>Discount: -{fmt(sale.discountAmount)}</div>
                    )}
                  </div>
                </div>

                {/* Expandable Items Section */}
                <div 
                  style={{ 
                    cursor: "pointer", 
                    padding: "8px 0", 
                    display: "flex", 
                    alignItems: "center", 
                    gap: 8,
                    color: "var(--text-3)",
                    fontSize: 11,
                    borderTop: "1px solid var(--border)",
                    marginTop: 8
                  }}
                  onClick={() => setExpandedSale(isExpanded ? null : sale.saleId)}
                >
                  <span>{isExpanded ? "▼" : "▶"}</span>
                  <span>{sale.items.length} item{sale.items.length !== 1 ? "s" : ""}</span>
                  {sale.ambassadorEmail && (
                    <span style={{ background: "var(--accent-lite)", padding: "2px 6px", borderRadius: 4 }}>
                      🏅 Ambassador tagged
                    </span>
                  )}
                </div>

                {/* Expanded Items Table */}
                {isExpanded && (
                  <div className="table-wrap" style={{ marginTop: 8 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th style={{ textAlign: "center" }}>Qty</th>
                          <th style={{ textAlign: "right" }}>Price</th>
                          <th style={{ textAlign: "right" }}>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sale.items.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.productName}</td>
                            <td style={{ textAlign: "center" }}>{item.quantity}</td>
                            <td style={{ textAlign: "right" }}>{fmt(item.sellingPrice)}</td>
                            <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(item.subtotal)}</td>
                          </tr>
                        ))}
                        {sale.discountAmount > 0 && (
                          <tr>
                            <td colSpan="3" style={{ textAlign: "right", color: "var(--red)" }}>Discount</td>
                            <td style={{ textAlign: "right", color: "var(--red)", fontWeight: 600 }}>-{fmt(sale.discountAmount)}</td>
                           </tr>
                        )}
                        <tr style={{ background: "var(--surface2)" }}>
                          <td colSpan="3" style={{ textAlign: "right", fontWeight: 700 }}>Total</td>
                          <td style={{ textAlign: "right", fontWeight: 800, color: "var(--accent)" }}>{fmt(sale.totalAmount)}</td>
                         </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ 
                  display: "flex", 
                  justifyContent: "flex-end", 
                  gap: 10, 
                  marginTop: 12,
                  paddingTop: 8,
                  borderTop: "1px solid var(--border)"
                }}>
                  <button 
                    className="btn-sm btn-secondary" 
                    onClick={() => printReceipt(sale)}
                  >
                    🖨️ Print Invoice
                  </button>
                  <button 
                    className="btn btn-success" 
                    onClick={() => process(sale.saleId)}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Processing..." : "✓ Confirm & Process"}
                  </button>
                  {isManager && (
                    <button 
                      className="btn btn-danger" 
                      onClick={() => cancel(sale.saleId)}
                      disabled={isProcessing}
                    >
                      ✕ Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PendingSales;
